// RealityAI Agent SSE client — PHASE K7 (client-side SSE consumer).
//
// K6 server SSE (AGENT_EVENTS.md, events_contract.SseWireFormat):
//   event: {name}
//   id: {seq}
//   data: {json}
//   (keep-alive: ": keep-alive" comment every ~15s, no seq)
//
// Owner decisions (binding):
//   OQ-4: fetch() + Authorization: Bearer — NOT EventSource, never
//         token-in-URL.
//   Reconnect (non-terminal): keep last accepted seq → backoff 1–2s →
//         reconnect with Last-Event-ID → replay → seq dedup → resume.
//   Reconnect (terminal): replay → terminal event → close (no more
//         reconnects after a terminal event).
//   Delivery model: at-least-once; dedup by (run_id, seq).
//
// Security: event data is DATA only — JSON.parsed, never eval/executed.

import { getToken } from "@/lib/api";
import type { AgentEvent } from "@/components/agents/types";

// ---------- Pure SSE wire parser (testable, framework-free) ----------

export interface SseFrame {
  event: string;
  id: string | null; // the `id:` field (seq as a string)
  data: string;
}

/**
 * Parse SSE text into complete frames. Returns the frames and the leftover
 * partial frame (to prepend to the next chunk). Handles the keep-alive
 * comment (`: keep-alive`) by silently dropping it (no frame).
 *
 * Per the SSE spec a frame is terminated by a blank line; multiple `data:`
 * lines join with \n (K6 sends a single data line per event — we stay
 * spec-correct anyway).
 */
export function parseSseChunk(buffer: string): { frames: SseFrame[]; rest: string } {
  // Normalize \r\n / \r to \n (spec: any of the three line endings).
  const text = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const parts = text.split("\n\n");
  const rest = parts.pop() ?? "";
  const frames: SseFrame[] = [];
  for (const part of parts) {
    const frame = parseSseFrame(part);
    if (frame) frames.push(frame);
  }
  return { frames, rest };
}

/** Parse one (blank-line-delimited) frame. Returns null for comments-only. */
export function parseSseFrame(raw: string): SseFrame | null {
  // Normalize line endings (spec: \r\n, \n, or \r) — parseSseChunk also does
  // this, but the frame parser must be correct standalone.
  const text = raw.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  let event = "message";
  let id: string | null = null;
  const dataLines: string[] = [];
  for (const line of text.split("\n")) {
    if (line.startsWith(":")) continue; // comment (keep-alive) — ignored
    const idx = line.indexOf(":");
    const field = idx === -1 ? line : line.slice(0, idx);
    let value = idx === -1 ? "" : line.slice(idx + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    switch (field) {
      case "event":
        if (value) event = value;
        break;
      case "id":
        id = value;
        break;
      case "data":
        dataLines.push(value);
        break;
      default:
        break; // unknown field — ignored (spec)
    }
  }
  if (dataLines.length === 0 && id === null) return null;
  return { event, id, data: dataLines.join("\n") };
}

/** Parse the JSON `data` payload of a frame (data-only, never executed). */
export function parseSseData(raw: string): Record<string, unknown> {
  try {
    const v: unknown = JSON.parse(raw);
    return v && typeof v === "object" && !Array.isArray(v)
      ? (v as Record<string, unknown>)
      : { value: v };
  } catch {
    return { text: raw };
  }
}

// ---------- Seq tracking (dedup + Last-Event-ID) ----------

/**
 * Per-run seq tracker (mirrors events_contract.SeqTracker semantics):
 * accept a seq only if it is strictly greater than the last accepted one;
 * `seq <= last` is a duplicate (at-least-once redelivery) → drop.
 */
export class SeqTracker {
  private lastSeq = -1;

  /** Returns true when the seq is a NEW forward event (accepted). */
  observe(seq: number): boolean {
    if (!Number.isInteger(seq) || seq < 0) return false;
    if (seq <= this.lastSeq) return false; // duplicate or regression → drop
    this.lastSeq = seq;
    return true;
  }

  /** The last accepted seq — sent as Last-Event-ID on reconnect. */
  lastEventId(): string | null {
    return this.lastSeq >= 0 ? String(this.lastSeq) : null;
  }

  reset(): void {
    this.lastSeq = -1;
  }
}

// ---------- Stream runner (fetch streaming + reconnect lifecycle) ----------

export interface AgentSseCallbacks {
  /** A new (deduped) event. `seq` is the per-run monotonic sequence. */
  onEvent?: (ev: AgentEvent) => void;
  /** The stream received a terminal event (run.completed/failed/cancelled). */
  onTerminal?: (ev: AgentEvent) => void;
  /** A network/HTTP failure happened (a reconnect may follow). */
  onError?: (err: Error) => void;
  /** The client will (re)connect after a disconnect (before the backoff). */
  onReconnecting?: () => void;
  /** The connection (re)established and is streaming. */
  onConnected?: () => void;
  /** The client gave up reconnecting (terminal or max retries). */
  onClose?: (reason: "terminal" | "aborted" | "retries_exhausted") => void;
}

export interface AgentSseOptions {
  runId: string;
  callbacks: AgentSseCallbacks;
  /** Reconnect backoff bounds (ms). Default: 1000–2000 (Owner §9/§10). */
  minBackoffMs?: number;
  maxBackoffMs?: number;
  /** Max reconnect attempts after a terminal-free failure (default 30). */
  maxRetries?: number;
  /** Injectable for tests. */
  fetchImpl?: typeof fetch;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

const DEFAULT_TERMINAL_EVENTS = new Set([
  "run.completed",
  "run.failed",
  "run.cancelled",
]);

const defaultSleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Connect to the run's SSE endpoint with fetch streaming + Bearer auth,
 * parse frames, dedup by seq, and own the reconnect lifecycle:
 *
 *  - non-terminal disconnect → backoff → reconnect with Last-Event-ID
 *    (server replays seq > Last-Event-ID, client dedups repeats);
 *  - terminal event → stop (no reconnect), close with reason "terminal";
 *  - abort() → close with reason "aborted".
 */
export class AgentSseClient {
  private opts: AgentSseOptions;
  private cb: AgentSseCallbacks;
  private tracker = new SeqTracker();
  private aborted = false;
  private terminalSeen = false;
  private retries = 0;
  private controllers: AbortController[] = [];

  constructor(opts: AgentSseOptions) {
    this.opts = opts;
    this.cb = opts.callbacks;
  }

  /** Start the stream (returns immediately; lifecycle runs async). */
  start(): void {
    void this.loop();
  }

  /** Stop the client (idempotent). Aborts every in-flight/connected fetch. */
  abort(): void {
    if (this.aborted) return;
    this.aborted = true;
    for (const c of this.controllers) c.abort();
    this.controllers = [];
  }

  get lastEventId(): string | null {
    return this.tracker.lastEventId();
  }

  get isAborted(): boolean {
    return this.aborted;
  }

  private get url(): string {
    return `/api/agent-runs/${this.opts.runId}/events`;
  }

  private async loop(): Promise<void> {
    const minBackoff = this.opts.minBackoffMs ?? 1000;
    const maxBackoff = this.opts.maxBackoffMs ?? 2000;
    const maxRetries = this.opts.maxRetries ?? 30;
    const now = this.opts.now ?? Date.now;
    const sleep = this.opts.sleep ?? defaultSleep;

    while (!this.aborted && !this.terminalSeen) {
      try {
        const res = await this.connect();
        if (!res.ok) {
          // Non-2xx before the stream (404 flag off / 403 / 409 / 501 / 5xx).
          // 404 (flag off) must not loop forever: surface + stop.
          const err = new Error(`SSE HTTP ${res.status}`);
          this.cb.onError?.(err);
          if (res.status === 404 || res.status === 403) {
            this.cb.onClose?.("retries_exhausted");
            return;
          }
          throw err;
        }
        if (!res.body) throw new Error("SSE: empty response body");
        this.cb.onConnected?.();
        await this.readBody(res.body);
        // Stream ended WITHOUT a terminal event (server closed) → fall
        // through to the reconnect path below.
      } catch (e) {
        if (this.aborted) break; // → onClose("aborted") below
        const err =
          e instanceof Error ? e : new Error(String(e));
        // AbortError = our own abort() → handled by the tail (onClose).
        if (err.name === "AbortError") break;
        this.cb.onError?.(err);
      }

      if (this.aborted || this.terminalSeen) break;

      // Reconnect path: keep the last accepted seq (Last-Event-ID),
      // backoff 1–2s, then retry.
      this.retries += 1;
      if (this.retries > maxRetries) {
        this.cb.onClose?.("retries_exhausted");
        return;
      }
      this.cb.onReconnecting?.();
      const jitter = now() % 1000;
      const delay = Math.min(
        maxBackoff,
        Math.max(minBackoff, minBackoff * this.retries) + (jitter % (maxBackoff - minBackoff + 1)),
      );
      await sleep(delay);
    }

    if (this.aborted) {
      this.cb.onClose?.("aborted");
    } else if (this.terminalSeen) {
      this.cb.onClose?.("terminal");
    }
  }

  private async connect(): Promise<Response> {
    const fetchImpl = this.opts.fetchImpl ?? fetch;
    const controller = new AbortController();
    this.controllers.push(controller);
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const lastEventId = this.tracker.lastEventId();
    if (lastEventId !== null) headers["Last-Event-ID"] = lastEventId;
    return fetchImpl(this.url, {
      method: "GET",
      headers,
      signal: controller.signal,
    });
  }

  private async readBody(body: ReadableStream<Uint8Array>): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { frames, rest } = parseSseChunk(buffer);
      buffer = rest;
      for (const frame of frames) {
        this.handleFrame(frame);
        if (this.terminalSeen || this.aborted) return;
      }
    }
  }

  private handleFrame(frame: SseFrame): void {
    if (frame.id === null) return; // no seq → not a run event (keep-alive)
    const seq = Number(frame.id);
    if (!Number.isInteger(seq)) return;
    // Dedup by seq (at-least-once safe): drop repeats/regressions.
    if (!this.tracker.observe(seq)) return;
    const ev: AgentEvent = {
      run_id: this.opts.runId,
      seq,
      event_name: frame.event,
      data: parseSseData(frame.data),
    };
    if (DEFAULT_TERMINAL_EVENTS.has(frame.event)) {
      this.terminalSeen = true;
      this.cb.onTerminal?.(ev);
    } else {
      this.cb.onEvent?.(ev);
    }
  }
}
